import "./run-mode.scss";
import { useState, useRef, useEffect, FormEvent, useContext } from "react";
import { Button, Form } from "react-bootstrap";
import { BsArrowLeft, BsSave } from "react-icons/bs";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import { IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { postSingleNonTable, getAllData } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { logLeveloptions } from "../../../shared/utils/static-values";
import { useBack } from "../../../shared/utils/use-back";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";
import { createUUID } from "../../../shared/utils/utils";

const fields: IColumn[] = [
  {
    key: "log_level",
    displayName: "Log level",
    dataType: {
      type: "enumeration",
      values: logLeveloptions,
    },
    controlled: true,
    placeholder: "debug"
  },
  {
    key: "output_dir",
    displayName: "Output directory",
    dataType: {
      type: "text",
    },
    controlled: true,
    placeholder: ""
  },
  {
    key: "aggregate_store_location",
    displayName: "Aggregate Store Location",
    dataType: {
      type: "text",
    },
    controlled: true,
    placeholder: ""
  },
  {
    key: "score_output_dir",
    displayName: "Score output directory",
    dataType: {
      type: "text",
    },
    controlled: true,
    placeholder: ""
  }
];

const RunMode = () => {
  const [validated, setValidated] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [data, setData] = useState({id: "", log_level: undefined, aggregate_store_location: undefined, output_dir: undefined});

  const authCtx = useContext(AuthContext);
  const formRef:any = useRef(); // eslint-disable-line

  const { flag, setFlag, next } = usePrompt(isDirty);
  const { sendRequest: postSingleRequest, status: allStatusPost, error: errorAllStatusPost } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getAllDataRequest, status: allStatus, data: loadedData, error: errorGet } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { handleBack } = useBack();

  useEffect(() => {
    if (allStatusPost === "completed" && !errorAllStatusPost) {
      authCtx.setOnboardingAnalyticsToggleHandler();
    }
  }, [allStatusPost]);

  useEffect(() => {
    if (allStatus === "completed" && !errorGet && !isDirty) {
      if (loadedData.rows.log_level !== data.log_level) {
        setIsDirty(true);
      } else if (loadedData.rows.aggregate_store_location !== data.aggregate_store_location && data.aggregate_store_location !== undefined) {
        setIsDirty(true);
      } else if (loadedData.rows.output_dir !== data.output_dir && data.output_dir !== undefined) {
        setIsDirty(true);
      }
    }
    if (data.log_level === "" || data.aggregate_store_location === "" || data.output_dir === "") {
      setIsDirty(true);
    }
  }, [data]);

  useEffect(() => {
    getAllDataRequest("runmodeconfig");
  }, []);

  useEffect(() => {
    if (allStatus === "completed" && !errorGet) {
      setData({...loadedData.rows});
    }
  }, [allStatus]);

  const handleOnChange = (targetVal: string | boolean | null, inputKey: string) => {
    setData((prevState) => ({ ...prevState, [inputKey]: targetVal }));
  };

  const onSaveHandler = () => {
    if (data.id) {
      postSingleRequest(`runmodeconfig/${data.id}`, { ...data });
    } else {
      const newRunModeId = createUUID();
      postSingleRequest(`runmodeconfig/${newRunModeId}`, { ...data, id: newRunModeId });
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity()) {
      onSaveHandler();
    }
    setValidated(true);
  };

  const externalValidation = () => {
    if (data.log_level && data.aggregate_store_location && data.output_dir) {
      onSaveHandler();
    } else {
      setValidated(true);
    }
  };

  const renderFields = () => {
    if (fields.length) {
      return (
        <RenderFields
          mode="aside"
          fields={fields}
          data={data}
          handleOnChange={handleOnChange}
        />
      );
    }
  };

  return (
    <div className="runMode">
      {flag && <ConfirmationPopup onSubmitFormId={"runModeForm"} show={flag} handleSaveClick={() => { externalValidation();}} handlePopupClosed={() => setFlag(false)} handleContinue={() => { next(); setFlag(false); }} />}

      <div onClick={() => handleBack()} className="listPage-nav-btn">
        <BsArrowLeft />
      </div>
      <div className="runMode-header">
        <h1 className="mt-2 mb-3">
          Run Mode Configuration
          <Tooltip
            content={"Global applications settings to configure running mode"}
            direction="top"
            delay={350}
          />
        </h1>
      </div>
      <div className="runMode-form">
        {validateLoadingStatusNew([allStatusPost, allStatus]) && <Spinner />}
        <Form ref={formRef} id="runModeForm"  validated={validated} onSubmit={handleSubmit}>
        
          <div className="runMode-buttonsGroup">
            <span className="runMode-subTitle">Settings info</span>
            <Button className="runMode-save" type="submit" variant="primary">
              <BsSave />
            </Button>
          </div>
          {renderFields()}          
        </Form>
      </div>
    </div>
  );
};

export default RunMode;
